import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams, HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { ITokens } from "@gloria-public/login/domain/interfaces/login.interface";
import { LoginDialogComponent } from "@gloria-public/login/views/login-dialog/login-dialog.component";
import { ConsentDialogComponent } from "@shared/components/consent-dialog/consent-dialog.component";
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  Subject,
  catchError,
  finalize,
  first,
  from,
  map,
  of,
  skipWhile,
  switchMap,
  takeUntil,
  tap,
  throwError,
} from "rxjs";
import { ConfigService } from "./config.service";
import { ConnectionEventsService } from "./connection-events.service";
import { SessionService } from "./session.service";

export interface GuestInitResult {
  uuid: string;
  created: boolean;
}

@Injectable({
  providedIn: "root",
})
export class ApiService {
  private baseHeader = new HttpHeaders({ "Content-Type": "application/json" });
  public refreshErrorMsg = "Session expired, need to reconnect";
  private logoutUrl = "user/logout";

  private loginDialogRef?: MatDialogRef<LoginDialogComponent>;
  private isRefreshing$ = new BehaviorSubject(false);
  private cancelRefreshFlow$ = new Subject<void>();
  private cancelPendingRequests$ = new Subject<void>();
  private suspendVerifyTokens = false;

  private consentDialogRef?: MatDialogRef<ConsentDialogComponent>;
  private isConsenting$ = new BehaviorSubject(false);
  private cancelConsentFlow$ = new Subject<void>();
  private cancelConsentRequests$ = new Subject<void>();
  private suspendConsent = false;

  private _baseUrl = "";

  get baseUrl(): string {
    return this._baseUrl;
  }

  constructor(
    private http: HttpClient,
    private sessionService: SessionService,
    private dialog: MatDialog,
    private configService: ConfigService,
    private connectionEventsService: ConnectionEventsService
  ) {
    this._baseUrl = this.configService.apiUrl;
    this.connectionEventsService.logout$.subscribe(() => {
      this.cancelVerifyTokens();
      this.cancelConsent();
    });
  }

  public appendGuestHeader(): Record<string, string> | undefined {
    if (!this.sessionService.isLoggedIn) {
      const guestUUID = this.sessionService.guestUUID;
      if (guestUUID) {
        return { "X-Guest-UUID": guestUUID };
      }
    }
    return undefined;
  }

  public getUpdatedPath(path: string): string {
    return this.sessionService.isLoggedIn ? path : `${path}/public`;
  }

  public get<T>(
    url: string,
    params?: Record<string, string | number | boolean>,
    isAuthRequired = true,
    headerParams?: Record<string, string | number | boolean>,
    responseType?: string
  ): Observable<T> {
    const options = {
      headers: this.baseHeader,
      params: new HttpParams(),
      responseType,
    };

    if (this.sessionService.accessToken && isAuthRequired) {
      options.headers = options.headers.set("Authorization", "Bearer " + this.sessionService.accessToken);
    }

    if (headerParams) {
      for (const k in headerParams) {
        if (headerParams[k]) {
          options.headers = options.headers.set(k, headerParams[k].toString());
        }
      }
    }

    if (params) {
      for (const k in params) {
        if (params[k]) {
          options.params = options.params.set(k, params[k].toString());
        }
      }
    }

    const request = (url: string, body: unknown, options: { headers: HttpHeaders; params: HttpParams }): Observable<T> => {
      return this.http.get<T>(url, options);
    };

    return request(this.baseUrl + "/" + url, undefined, options).pipe(
      first(),
      catchError((err) => {
        if (err.status === 401) {
          const message = err?.error;
          if (message === "CONSENT_REQUIRED") {
            return this.handleConsentRequiredAndRedoRequest(request, url, undefined, options);
          } else if (isAuthRequired) {
            return this.verifyTokensAndRedoRequest(request, url, undefined, options);
          }
        }
        return throwError(() => err);
      })
    );
  }

  public getBlobAsFullResponse<T>(url: string, params?: Record<string, string | number | boolean>, isAuthRequired = true): Observable<T> {
    const options = {
      headers: this.baseHeader,
      params: new HttpParams(),
      responseType: "blob",
      observe: "response",
    };

    if (this.sessionService.accessToken && isAuthRequired) {
      options.headers = options.headers.set("Authorization", "Bearer " + this.sessionService.accessToken);
    }

    if (params) {
      for (const k in params) {
        if (params[k]) {
          options.params = options.params.set(k, params[k].toString());
        }
      }
    }

    const request = (url: string, body: unknown, options: { headers: HttpHeaders; params: HttpParams }): Observable<T> => {
      return this.http.get<T>(url, options);
    };

    return request(this.baseUrl + "/" + url, undefined, options).pipe(
      first(),
      catchError((err: HttpErrorResponse) => {
        if (err.status === 401) {
          return this.extractErrorMessage(err).pipe(
            switchMap((message) => {
              if (message === "CONSENT_REQUIRED") {
                return this.handleConsentRequiredAndRedoRequest(request, url, undefined, options);
              } else if (isAuthRequired) {
                return this.verifyTokensAndRedoRequest(request, url, undefined, options);
              }
              return throwError(() => err);
            })
          );
        }
        return throwError(() => err);
      })
    );
  }

  /** Handles both blob and non-blob error bodies safely
   * @param err The error response
   * @return the error message
   */
  private extractErrorMessage(err: HttpErrorResponse): Observable<string> {
    if (err.error instanceof Blob) {
      return from(err.error.text());
    }
    return of(err.error);
  }

  public post<T>(
    url: string,
    body: unknown,
    headerParams?: Record<string, string | number | boolean>,
    isAuthRequired = true,
    params?: Record<string, string | number | boolean>,
    responseType?: string
  ): Observable<T> {
    const options = {
      headers: new HttpHeaders(),
      params: new HttpParams(),
      responseType,
    };

    if (this.sessionService.accessToken && isAuthRequired) {
      options.headers = options.headers.set("Authorization", "Bearer " + this.sessionService.accessToken);
    } else if (this.sessionService.accountCreationToken && isAuthRequired) {
      options.headers = options.headers.set("Authorization", "Bearer " + this.sessionService.accountCreationToken);
    }

    if (headerParams) {
      for (const k in headerParams) {
        if (headerParams[k]) {
          options.headers = options.headers.set(k, headerParams[k].toString());
        }
      }
    }

    if (params) {
      for (const k in params) {
        if (params[k]) {
          options.params = options.params.set(k, params[k].toString());
        }
      }
    }

    const request = (url: string, body: unknown, options: { headers: HttpHeaders; params: HttpParams }): Observable<T> => {
      return this.http.post<T>(url, body, options);
    };

    return request(this.baseUrl + "/" + url, body, options).pipe(
      first(),
      catchError((err) => {
        if (err.status === 401) {
          const message = err?.error;
          if (message === "CONSENT_REQUIRED") {
            return this.handleConsentRequiredAndRedoRequest(request, url, body, options);
          } else if (isAuthRequired) {
            return this.verifyTokensAndRedoRequest(request, url, body, options);
          }
        }
        return throwError(() => err);
      })
    );
  }

  public put<T>(url: string, body: unknown, isAuthRequired = true): Observable<T> {
    const options = {
      headers: this.baseHeader,
      params: new HttpParams(),
    };

    if (this.sessionService.accessToken && isAuthRequired) {
      options.headers = options.headers.set("Authorization", "Bearer " + this.sessionService.accessToken);
    }
    const request = (url: string, body: unknown, options: { headers: HttpHeaders; params: HttpParams }): Observable<T> => {
      return this.http.put<T>(url, body, options);
    };

    return request(this.baseUrl + "/" + url, body, options).pipe(
      first(),
      catchError((err) => {
        if (err.status === 401) {
          const message = err?.error;
          if (message === "CONSENT_REQUIRED") {
            return this.handleConsentRequiredAndRedoRequest(request, url, body, options);
          } else if (isAuthRequired) {
            return this.verifyTokensAndRedoRequest(request, url, body, options);
          }
        }
        return throwError(() => err);
      })
    );
  }

  public patch<T>(
    url: string,
    body: unknown,
    isAuthRequired = true,
    headerParams?: Record<string, string | number | boolean>,
    params?: Record<string, string | number | boolean>
  ): Observable<T> {
    const options = {
      headers: this.baseHeader,
      params: new HttpParams(),
    };

    if (this.sessionService.accessToken && isAuthRequired) {
      options.headers = options.headers.set("Authorization", "Bearer " + this.sessionService.accessToken);
    }

    if (headerParams) {
      for (const k in headerParams) {
        if (headerParams[k]) {
          options.headers = options.headers.set(k, headerParams[k].toString());
        }
      }
    }

    if (params) {
      for (const k in params) {
        if (params[k]) {
          options.params = options.params.set(k, params[k].toString());
        }
      }
    }

    const request = (url: string, body: unknown, options: { headers: HttpHeaders; params: HttpParams }): Observable<T> => {
      return this.http.patch<T>(url, body, options);
    };

    return request(this.baseUrl + "/" + url, body, options).pipe(
      first(),
      catchError((err) => {
        if (err.status === 401) {
          const message = err?.error;
          if (message === "CONSENT_REQUIRED") {
            return this.handleConsentRequiredAndRedoRequest(request, url, body, options);
          } else if (isAuthRequired) {
            return this.verifyTokensAndRedoRequest(request, url, body, options);
          }
        }
        return throwError(() => err);
      })
    );
  }

  public patchResponse<T>(
    url: string,
    body: unknown,
    isAuthRequired = true,
    headerParams?: Record<string, string | number | boolean>,
    params?: Record<string, string | number | boolean>
  ): Observable<HttpResponse<T>> {
    const options = {
      headers: this.baseHeader,
      params: new HttpParams(),
    };

    if (this.sessionService.accessToken && isAuthRequired) {
      options.headers = options.headers.set("Authorization", "Bearer " + this.sessionService.accessToken);
    }

    if (headerParams) {
      for (const k in headerParams) {
        if (headerParams[k]) {
          options.headers = options.headers.set(k, headerParams[k].toString());
        }
      }
    }

    if (params) {
      for (const k in params) {
        if (params[k]) {
          options.params = options.params.set(k, params[k].toString());
        }
      }
    }

    const request = (
      url: string,
      body: unknown,
      options: {
        headers: HttpHeaders;
        params: HttpParams;
      }
    ): Observable<HttpResponse<T>> => {
      return this.http.patch<T>(url, body, {
        ...options,
        observe: "response",
      });
    };

    return request(this.baseUrl + "/" + url, body, options).pipe(
      first(),
      catchError((err) => {
        if (err.status === 401) {
          const message = err?.error;
          if (message === "CONSENT_REQUIRED") {
            return this.handleConsentRequiredAndRedoRequest(request, url, body, options);
          } else if (isAuthRequired) {
            return this.verifyTokensAndRedoRequest(request, url, body, options);
          }
        }

        return throwError(() => err);
      })
    );
  }

  public delete<T>(
    url: string,
    params?: Record<string, string | number | boolean>,
    isAuthRequired = true,
    headerParams?: Record<string, string | number | boolean>
  ): Observable<T> {
    const options: { headers: HttpHeaders; params: HttpParams } = {
      headers: this.baseHeader,
      params: new HttpParams(),
    };

    if (this.sessionService.accessToken && isAuthRequired) {
      options.headers = options.headers.set("Authorization", "Bearer " + this.sessionService.accessToken);
    }

    if (headerParams) {
      for (const k in headerParams) {
        if (headerParams[k]) {
          options.headers = options.headers.set(k, headerParams[k].toString());
        }
      }
    }

    if (params) {
      for (const k in params) {
        if (params[k]) {
          options.params = options.params.set(k, params[k].toString());
        }
      }
    }

    const request = (url: string, body: unknown, options: { headers: HttpHeaders; params: HttpParams }): Observable<T> => {
      return this.http.delete<T>(url, options);
    };

    return request(this.baseUrl + "/" + url, undefined, options).pipe(
      first(),
      catchError((err) => {
        if (err.status === 401) {
          const message = err?.error;
          if (message === "CONSENT_REQUIRED") {
            return this.handleConsentRequiredAndRedoRequest(request, url, undefined, options);
          } else if (isAuthRequired) {
            return this.verifyTokensAndRedoRequest(request, url, undefined, options);
          }
        }
        return throwError(() => err);
      })
    );
  }

  /**
   * Handles 401 authentication failures by either:
   * - waiting for an ongoing token refresh to complete, or
   * - triggering a refresh flow and retrying the original request.
   *
   * Depending on the refresh result, it may retry the request,
   * open a login dialog, redirect to login, or propagate an error.
   */
  private verifyTokensAndRedoRequest<R>(
    request: (url: string, body: unknown, options: { headers: HttpHeaders; params: HttpParams }) => Observable<R>,
    url: string,
    body: unknown,
    options: { headers: HttpHeaders; params: HttpParams }
  ): Observable<R> {
    /* private verifyTokensAndRedoRequest<T>(
    request: (url: string, body: unknown, options: { headers: HttpHeaders; params: HttpParams }) => Observable<T>,
    url: string,
    body: unknown,
    options: { headers: HttpHeaders; params: HttpParams }
  ): Observable<T> { */
    // Deal with suspended refreshes during handle-link
    if (this.suspendVerifyTokens) {
      return EMPTY;
    }

    if (this.isRefreshing$.value) {
      // Don't pipe logout, to avoid reconnection for the refresh, then directly log out
      if (url === this.logoutUrl) {
        return throwError(() => ({ message: "Logout cancelled" }));
      }
      // retry all the request when the refresh is done
      return this.isRefreshing$.pipe(
        skipWhile((v) => v),
        first(),
        takeUntil(this.cancelPendingRequests$),
        switchMap(() => {
          options.headers = options.headers.set("Authorization", "Bearer " + this.sessionService.accessToken);
          return request(this.baseUrl + "/" + url, body, options);
        })
      );
    } else {
      // token expired, we try to refresh it with the refresh token
      this.isRefreshing$.next(true);
      return this.refreshToken().pipe(
        first(),
        takeUntil(this.cancelRefreshFlow$),
        switchMap((newTokens) => {
          // token refreshed
          this.sessionService.accessToken = newTokens.accessToken as string;
          this.sessionService.refreshToken = newTokens.refreshToken as string;
          options.headers = options.headers.set("Authorization", "Bearer " + this.sessionService.accessToken);
          // retry the request
          return request(this.baseUrl + "/" + url, body, options).pipe(
            catchError((requestError) => {
              // Error while redoing the request (refresh itself was OK):
              // mark it so it's simply rethrown below, with no side effects.
              requestError.__isRedoRequestError = true;
              return throwError(() => requestError);
            })
          );
        }),
        catchError((error) => {
          if (error?.__isRedoRequestError) {
            delete error.__isRedoRequestError;
            // Just propagate the redo-request error, no logout/navigation.
            return throwError(() => error);
          }

          // From here on: this is really a refreshToken() failure.
          const refreshError = error;
          if (refreshError.status === 401) {
            if (url === this.logoutUrl) {
              this.connectionEventsService.notifyLogout();
            } else {
              this.loginDialogRef = this.dialog.open(LoginDialogComponent, {
                autoFocus: false,
                disableClose: true,
              });
              return this.loginDialogRef.afterClosed().pipe(
                first(),
                takeUntil(this.cancelRefreshFlow$),
                finalize(() => {
                  this.loginDialogRef = undefined;
                }),
                switchMap((loggedIn: boolean) => {
                  if (!loggedIn) {
                    return EMPTY;
                  }
                  options.headers = options.headers.set("Authorization", "Bearer " + this.sessionService.accessToken);
                  return request(this.baseUrl + "/" + url, body, options);
                })
              );
            }
            throw new Error(this.refreshErrorMsg);
          } else {
            this.connectionEventsService.notifyLogout("page.login.logoutCanceled");
            throw refreshError;
          }
        }),
        finalize(() => {
          this.isRefreshing$.next(false);
        })
      );
    }
  }

  /**
   * Closes the login dialog and cancels ongoing token refresh flows and requests.
   */
  public cancelVerifyTokens(): void {
    this.loginDialogRef?.close(false);

    this.cancelRefreshFlow$.next();
    this.cancelPendingRequests$.next();
  }

  /**
   * Suspends token verification by cancelling any active refresh flow.
   * Must always be followed by resumeVerifyTokensFlow() to re-enable token handling.
   */
  public suspendVerifyTokensFlow(): void {
    this.cancelVerifyTokens();
    this.suspendVerifyTokens = true;
  }

  /**
   * Resumes token verification after it has been suspended.
   */
  public resumeVerifyTokensFlow(): void {
    this.suspendVerifyTokens = false;
  }

  /**
   * Requests a new access token using the refresh token.
   * @return the new access and refresh tokens
   */
  // Note: can't be placed in auth.service othervise circular dependence
  private refreshToken(): Observable<ITokens> {
    let headers = this.baseHeader;
    if (this.sessionService.refreshToken) {
      headers = headers.set("Authorization", "Bearer " + this.sessionService.refreshToken);
    }
    return this.http.post(this.baseUrl + "/user/auth/refresh", undefined, { headers }) as Observable<ITokens>;
  }

  /**
   * Ensures user consent before executing the request.
   * Queues or retries requests depending on consent state.
   */
  private handleConsentRequiredAndRedoRequest<R>(
    request: (url: string, body: unknown, options: { headers: HttpHeaders; params: HttpParams }) => Observable<R>,
    url: string,
    body: unknown,
    options: { headers: HttpHeaders; params: HttpParams }
  ): Observable<R> {
    /* private handleConsentRequiredAndRedoRequest<T>(
    request: (url: string, body: unknown, options: { headers: HttpHeaders; params: HttpParams }) => Observable<T>,
    url: string,
    body: unknown,
    options: { headers: HttpHeaders; params: HttpParams }
  ): Observable<T> { */
    // Deal with suspended consent during handle-link
    if (this.suspendConsent) {
      return EMPTY;
    }

    if (this.isConsenting$.value) {
      // retry all the request when the consent is accepted
      return this.isConsenting$.pipe(
        skipWhile((v) => v),
        first(),
        takeUntil(this.cancelConsentRequests$),
        switchMap(() => request(this.baseUrl + "/" + url, body, options))
      );
    } else {
      // new consent available and not yet accepted
      this.isConsenting$.next(true);

      this.consentDialogRef = this.dialog.open(ConsentDialogComponent, {
        disableClose: true,
        width: "90vw",
        maxWidth: "1200px",
        maxHeight: "90vh",
        autoFocus: false,
        restoreFocus: false,
      });
      return this.consentDialogRef.afterClosed().pipe(
        first(),
        takeUntil(this.cancelConsentFlow$),
        finalize(() => {
          this.consentDialogRef = undefined;
        }),
        switchMap((accepted: boolean) => {
          if (!accepted) {
            return throwError(() => new Error("CONSENT_NOT_ACCEPTED"));
          }
          return request(this.baseUrl + "/" + url, body, options);
        }),
        finalize(() => this.isConsenting$.next(false))
      );
    }
  }

  /**
   * Closes the consent dialog and cancels ongoing consent flows and requests.
   */
  public cancelConsent(): void {
    this.consentDialogRef?.close(false);

    this.cancelConsentFlow$.next();
    this.cancelConsentRequests$.next();
  }

  /**
   * Suspends the consent flow by cancelling any active consent process.
   * Must always be followed by resumeConsentFlow() to re-enable consent handling.
   */
  public suspendConsentFlow(): void {
    this.cancelConsent();
    this.suspendConsent = true;
  }

  /**
   * Resumes the consent flow after it has been suspended.
   */
  public resumeConsentFlow(): void {
    this.suspendConsent = false;
  }

  /**
   * Creates a new guest account
   * @param language language of the guest
   * @return guestUUID
   */
  // Note: can't be placed in registration.service othervise circular dependence
  private createGuest(language: string): Observable<string> {
    const params = new HttpParams().set("langCode", language);

    return this.http.post(this.baseUrl + "/clients/guest", null, {
      params,
      responseType: "text",
    }) as Observable<string>;
  }

  /**
   * Returns an existing guest UUID or creates a new one and stores it in the session.
   */
  private getGuestUUID(): Observable<GuestInitResult> {
    const existing = this.sessionService.guestUUID;

    if (existing) {
      return of({
        uuid: existing,
        created: false,
      });
    }

    return this.createGuest(this.sessionService.userLang).pipe(
      tap((uuid: string) => {
        this.sessionService.guestUUID = uuid;
      }),
      map((uuid) => ({
        uuid,
        created: true,
      }))
    );
  }

  /**
   * Ensures a guest UUID is initialized when the user is not logged in.
   */
  public initGuestIfRequired(): Observable<GuestInitResult | null> {
    if (this.sessionService.isLoggedIn) {
      return of(null);
    }

    return this.getGuestUUID();
  }
}
